// What the store says about a game: its US price, and the details beside the
// description at the top of its page. Nothing here touches the DOM.

import type { AppCompany, Platform, Price, StoreInfo } from "../../shared/api.ts";
import { COMPANY_ROLES, type CompanyRole, companySlug } from "../../shared/companies.ts";
import { companyPath } from "../../shared/site.ts";
import { externalLink, plural } from "./markup.ts";
import { escapeHtml } from "./web.ts";

export const PLATFORM_NAMES: Readonly<Record<Platform, string>> = {
  windows: "Windows",
  mac: "macOS",
  linux: "Linux"
};

// US cents as dollars ("$59.99"), whatever the reader's locale: the store
// prices are in US dollars.
const usd = /* @__PURE__ */ new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
export const dollars = (cents: number): string => usd.format(cents / 100);

// A discount as the store writes it: "−50%".
export const discountText = (percent: number): string => `−${percent}%`;

// A discount in words: "50% off".
export const percentOff = (percent: number): string => `${percent}% off`;

// A price as markup: just the final price at full price, else the full
// price struck through beside it and the sale badge. The game page's price
// and the on-sale rows' figure share it.
export function priceMarkup(initial: number, final: number, discount: number): string {
  return discount > 0
    ? `<s class="muted">${dollars(initial)}</s> ${dollars(final)}
       <span class="sale-badge">${discountText(discount)}</span>`
    : dollars(final);
}

// The "Price" store fact: the US price, struck through beside the sale price
// while it's discounted, and the lowest it has sold for since prices were
// first checked when that's lower than now. A game with no price is "Free to
// play" when the store says it's free, and has no fact otherwise (not out
// yet, or never checked).
export function priceFact(price: Price | null, store: StoreInfo | null): string | null {
  if (!price) return store?.isFree ? "Free to play" : null;

  const now = priceMarkup(price.initial, price.final, price.discount);
  const lowest =
    price.lowest < price.final
      ? `<small class="muted price-low">Lowest seen ${dollars(price.lowest)}</small>`
      : "";

  return now + lowest;
}

// The store's facts about the game (store_info in migrations/), in the page's
// head beside the description (under it on narrower screens), each only if
// Steam gave it; nothing at all until the crawler has fetched them. A
// developer or publisher with a page (the API's `companies`) links to it.
// The price (see priceFact) comes first, shown even before the store details.
export function storeFacts(
  store: StoreInfo | null,
  companies: readonly AppCompany[] = [],
  price: Price | null = null
): string {
  const facts: string[] = [];
  const fact = (label: string, html: string): number =>
    facts.push(`<div><dt class="stat-label">${label}</dt><dd>${html}</dd></div>`);
  const priced = priceFact(price, store);
  if (priced) fact("Price (US)", priced);
  if (!store) return factList(facts);
  // "Developer" or "Developers", as the list needs.
  const names = (label: string, items: readonly string[]): void => {
    if (items.length) fact(plural(items.length, label), items.map(escapeHtml).join(", "));
  };
  // The same for a company `role`, each name with a page linking to it.
  const companyNames = (role: CompanyRole, items: readonly string[]): void => {
    if (!items.length) return;
    const { label } = COMPANY_ROLES[role];
    const linked = items.map((name) => {
      const slug = companySlug(name);
      return companies.some((c) => c.role === role && c.slug === slug)
        ? `<a href="${companyPath(role, slug)}" data-nav>${escapeHtml(name)}</a>`
        : escapeHtml(name);
    });
    fact(plural(items.length, label), linked.join(", "));
  };

  companyNames("developer", store.developers);
  // Only when someone else published it; most studios self-publish.
  if (store.publishers.join() !== store.developers.join()) {
    companyNames("publisher", store.publishers);
  }
  if (store.releaseDate) {
    fact(store.comingSoon ? "Release" : "Released", escapeHtml(store.releaseDate));
  }
  names("Genre", store.genres);
  if (store.platforms.length) {
    fact("Platforms", store.platforms.map((p) => PLATFORM_NAMES[p]).join(", "));
  }
  if (store.metacritic) {
    const score = String(store.metacritic.score);
    fact(
      "Metacritic",
      store.metacritic.url ? externalLink(store.metacritic.url, score) : score
    );
  }
  if (store.website) {
    let host: string;
    try {
      host = new URL(store.website).hostname.replace(/^www\./, "");
    } catch {
      host = store.website;
    }
    fact("Website", externalLink(store.website, host));
  }

  return factList(facts);
}

// storeFacts' facts as their list; nothing when there are none.
const factList = (facts: readonly string[]): string =>
  facts.length ? `<dl class="store-facts" aria-label="Game details">${facts.join("")}</dl>` : "";
